import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BEHAVIOR_HELP, BEHAVIOR_LABELS, FREQUENCY_LABELS } from '@balance/domain';
import { api, type ConceptDetail } from '../api.js';
import { dateLabel, eur, fmtDateTime, periodLabel, shortDateLabel } from '../format.js';
import { useApp } from '../state.js';
import { Modal } from './Modal.js';
import { Badge } from './ui.js';

/**
 * Ficha de reglas de solo lectura (C03). Se abre desde cualquier nombre de concepto.
 * La edición de comportamiento sólo está en Configuración de conceptos.
 */
export function ConceptSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const { catalog, balance } = useApp();
  const [detail, setDetail] = useState<ConceptDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    setDetail(null);
    api
      .concept(id)
      .then((d) => alive && setDetail(d))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Error'));
    return () => {
      alive = false;
    };
  }, [id]);
  const concept = detail?.concept ?? catalog?.concepts.find((c) => c.id === id);
  const category = catalog?.categories.find((c) => c.id === concept?.categoryId);
  const person = catalog?.persons.find((p) => p.id === concept?.personId);
  const row = balance?.rows.find((r) => r.conceptId === id);
  const upcoming = detail?.occurrences.filter((o) => balance && o.period >= balance.currentPeriod).slice(0, 14) ?? [];
  const past = detail?.occurrences.filter((o) => balance && o.period < balance.currentPeriod).slice(-6) ?? [];
  return (
    <Modal title={concept?.name ?? 'Concepto'} subtitle="Reglas de comportamiento · solo lectura" onClose={onClose} side>
      {error ? <div className="notice error">{error}</div> : null}
      {!concept ? <div className="skeleton" /> : null}
      {concept ? (
        <>
          <div className="inline" style={{ marginBottom: 16 }}>
            <Badge>{BEHAVIOR_LABELS[concept.behavior]}</Badge>
            <Badge kind="neutral">{concept.direction === 'income' ? 'Ingreso' : 'Egreso'}</Badge>
            {category ? <Badge kind="neutral">{category.name}</Badge> : null}
            {person ? <Badge kind="neutral">{person.name}</Badge> : null}
            {row ? <Badge kind={row.activeNow ? '' : 'warn'}>{row.activeNow ? 'Activo' : 'Sin vigencia hoy'}</Badge> : null}
          </div>
          <p className="muted">{BEHAVIOR_HELP[concept.behavior]}</p>
          {concept.notes ? <p>{concept.notes}</p> : null}

          <h3 style={{ marginTop: 18 }}>Condiciones y vigencias</h3>
          {!detail ? <div className="skeleton" /> : null}
          <div className="timeline">
            {detail?.timeline.map((t) => (
              <div key={t.version.id} className={t.version.state === 'inactive' ? 'inactive' : ''}>
                <strong>{t.title}</strong> <span className="muted">desde {dateLabel(t.from)}{t.to ? ` hasta ${dateLabel(t.to)}` : ' · vigente'}</span>
                <ul className="muted">
                  {t.details.map((d, i) => (
                    <li key={i}>{d}</li>
                  ))}
                  {t.version.frequency ? <li>Frecuencia: {FREQUENCY_LABELS[t.version.frequency]}</li> : null}
                </ul>
              </div>
            ))}
          </div>

          {detail && detail.overrides.length ? (
            <>
              <h3>Ajustes de un solo mes</h3>
              {detail.overrides.map((o) => (
                <div className="row" key={o.id}>
                  <div>
                    {periodLabel(o.period)}
                    {o.note ? <div className="muted">{o.note}</div> : null}
                  </div>
                  <span className="number">{eur(o.amountCents)}</span>
                </div>
              ))}
            </>
          ) : null}

          {detail && (upcoming.length || detail.cancelled.length) ? (
            <>
              <h3 style={{ marginTop: 18 }}>Próximos vencimientos</h3>
              {upcoming.map((o) => (
                <div className="row" key={o.key}>
                  <div>
                    {dateLabel(o.date)}
                    {o.status === 'moved' ? <span className="badge warn" style={{ marginLeft: 8 }}>movida desde {shortDateLabel(o.originalDate)}</span> : null}
                    {o.status === 'added' ? <span className="badge" style={{ marginLeft: 8 }}>añadida</span> : null}
                    {o.instalmentNumber ? <span className="badge neutral" style={{ marginLeft: 8 }}>cuota {o.instalmentNumber}/{o.instalmentTotal}</span> : null}
                    <div className="muted">Se imputa a {periodLabel(o.period)}{o.servicePeriod !== o.period ? ` (mes de servicio ${periodLabel(o.servicePeriod)})` : ''}</div>
                  </div>
                  <span className="number">{eur(o.amountCents)}</span>
                </div>
              ))}
              {detail.cancelled.map((c) => (
                <div className="row dim" key={c.key}>
                  <div>
                    <s>{dateLabel(c.originalDate)}</s> <span className="badge red">cancelada</span>
                    {c.note ? <div className="muted">{c.note}</div> : null}
                  </div>
                  <span className="number muted">{eur(c.amountCents)}</span>
                </div>
              ))}
            </>
          ) : null}
          {detail && past.length ? (
            <>
              <h3 style={{ marginTop: 18 }}>Vencimientos recientes</h3>
              {past.map((o) => (
                <div className="row dim" key={o.key}>
                  <div>{dateLabel(o.date)}</div>
                  <span className="number">{eur(o.amountCents)}</span>
                </div>
              ))}
            </>
          ) : null}

          {detail && detail.audit.length ? (
            <>
              <h3 style={{ marginTop: 18 }}>Historial de cambios</h3>
              {detail.audit.slice(0, 12).map((a) => (
                <div className="row" key={a.id}>
                  <div>
                    <span className="badge neutral">{a.entity.replace('concept_', '')}</span> {a.action}
                    <div className="muted">{fmtDateTime(a.at)} · {a.actor}</div>
                  </div>
                </div>
              ))}
            </>
          ) : null}

          <div className="hint" style={{ marginTop: 20 }}>
            Aquí no se edita nada. Para cambiar comportamiento, precio o calendario desde una fecha, ir a{' '}
            <Link to={`/conceptos?editar=${concept.id}`} onClick={onClose}>
              Configuración de conceptos
            </Link>
            .
          </div>
        </>
      ) : null}
    </Modal>
  );
}
